import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import {
  Coins,
  Lock,
  TrendingUp,
  Zap,
  Shield,
  AlertCircle,
  Info,
  Play,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const COLORS = {
  primary: "#00D2FF",
  secondary: "#00FF94",
  purple: "#A855F7",
  orange: "#F97316",
  blue: "#3B82F6"
};

const Credits = () => {
  const { language } = useLanguage();
  const [neuroAmount, setNeuroAmount] = useState([10000]);
  const [lockDuration, setLockDuration] = useState("30");
  const [networkLoad, setNetworkLoad] = useState("normal");
  const [simulated, setSimulated] = useState(false);
  const [simulatedMetrics, setSimulatedMetrics] = useState(null);
  
  const t = (text) => translate(text, language);

  const runSimulation = () => {
    // Calculate simulated values based on inputs
    const amount = neuroAmount[0];
    const duration = parseInt(lockDuration);
    const loadMultiplier = networkLoad === "low" ? 1.2 : networkLoad === "high" ? 0.8 : 1.0;
    
    const baseCredits = amount * 10 * loadMultiplier;
    const durationBonus = duration / 30; // Longer locks get more credits
    const totalCredits = Math.floor(baseCredits * durationBonus);

    setSimulatedMetrics({
      creditsMinted: totalCredits.toLocaleString(),
      costPerInference: (amount / totalCredits).toFixed(4),
      robotsServiced: Math.floor(totalCredits / 1000),
      agentTasks: Math.floor(totalCredits / 50),
      gpuSeconds: Math.floor(totalCredits * 2.5).toLocaleString(),
      proofs: Math.floor(totalCredits * 1.2).toLocaleString(),
      feeBurned: (amount * 0.03).toLocaleString(),
      creditsRemaining: Math.floor(totalCredits * 0.85).toLocaleString()
    });

    setSimulated(true);
    toast.success("Simulation complete (Testnet preview only)");
  };

  // Chart data
  const conversionCurveData = Array.from({ length: 10 }, (_, i) => {
    const neuro = i * 5000;
    return {
      neuro,
      low: neuro * 12,
      normal: neuro * 10,
      high: neuro * 8
    };
  });

  const supplyFlowData = Array.from({ length: 12 }, (_, i) => ({
    month: `M${i + 1}`,
    locked: 10000 + i * 2000,
    issued: 100000 + i * 20000,
    burned: 50000 + i * 8000,
    recycled: 30000 + i * 5000
  }));

  const consumptionData = [
    { name: t("Inference"), value: 35, fill: COLORS.primary },
    { name: t("Training"), value: 25, fill: COLORS.secondary },
    { name: t("Simulation"), value: 20, fill: COLORS.purple },
    { name: t("Marketplace"), value: 10, fill: COLORS.orange },
    { name: t("Runtime"), value: 7, fill: COLORS.blue },
    { name: t("Audit"), value: 3, fill: "#888" }
  ];

  return (
    <div className="space-y-6" data-testid="credits-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t("Compute Credits")}</h1>
        <p className="text-gray-400 mb-4">
          {t("Programmable on-robot compute units backed by the $NEURO token")}
        </p>
        
        {/* Testnet Warning Banner */}
        <Card className="bg-yellow-500/5 border-yellow-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-300 mb-1">{t("⚠️ Testnet Preview")}</p>
                <p className="text-sm text-gray-300">
                  {t("This dashboard simulates Compute Credit minting mechanics and supply flows.")} 
                  <strong className="text-yellow-200"> {t("$NEURO to Credit conversion is not yet active.")}</strong> {t("No tokens can be locked or burned at this stage.")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* What Are Compute Credits */}
      <Card className="bg-[#0A0A0A] border-cyan-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-cyan-400" />
            {t("What Are Compute Credits?")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-300 mb-4">
            {t("Compute Credits are non-transferable network units that will represent:")}
          </p>
          <ul className="space-y-2 text-gray-300 ml-4">
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              <span>{t("Verified inference execution")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              <span>{t("Training epochs contributed by robots")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              <span>{t("Simulation runs inside Cerebro and RoboMesh")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              <span>{t("Agent marketplace usage")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              <span>{t("Attested GPU or edge compute routed through NeuroMesh")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">•</span>
              <span>{t("Proof of Inference and Proof of Action receipts")}</span>
            </li>
          </ul>
          
          <div className="mt-6 p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
            <p className="text-sm text-gray-300">
              <strong className="text-purple-300">{t("Once live,")}</strong> {t("Compute Credits will be minted by locking $NEURO into protocol contracts.")}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {t("Each wallet will permanently display: Total credits minted • Credits consumed • Credits remaining • Lifetime network contribution • Robot nodes serviced • Agent workflows executed")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Immutable Counter */}
      <Card className="bg-gradient-to-br from-[#0A0A0A] to-purple-950/20 border-purple-500/30">
        <CardContent className="p-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
            <Lock className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-mono text-purple-300 uppercase tracking-wider">{t("Immutable Once Live")}</span>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">{t("Lifetime Compute Credits")}</h2>
          <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 mb-4 font-mono">
            0
          </div>
          
          <p className="text-sm text-gray-400 max-w-2xl mx-auto">
            {t("This counter becomes")} <strong className="text-purple-300">{t("immutable once minting is enabled on mainnet")}</strong>. 
            {t("It will permanently record every Compute Credit ever minted by this wallet. During testnet, this value is simulated.")}
          </p>
        </CardContent>
      </Card>

      {/* Minting Simulator */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Play className="w-5 h-5 text-cyan-400" />
            {t("Minting Simulator (Testnet Preview)")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* $NEURO Slider */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-300">{t("$NEURO to Lock")}</label>
              <span className="text-lg font-bold text-cyan-400 font-mono">{neuroAmount[0].toLocaleString()}</span>
            </div>
            <Slider
              value={neuroAmount}
              onValueChange={setNeuroAmount}
              max={50000}
              step={500}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>50,000</span>
            </div>
          </div>

          {/* Lock Duration */}
          <div>
            <label className="text-sm font-medium text-gray-300 block mb-2">{t("Lock Duration")}</label>
            <Select value={lockDuration} onValueChange={setLockDuration}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="7">{t("7 days")}</SelectItem>
                <SelectItem value="30">{t("30 days")}</SelectItem>
                <SelectItem value="90">{t("90 days")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Network Load */}
          <div>
            <label className="text-sm font-medium text-gray-300 block mb-2">{t("Network Load Mode")}</label>
            <Select value={networkLoad} onValueChange={setNetworkLoad}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="low">{t("Low")}</SelectItem>
                <SelectItem value="normal">{t("Normal")}</SelectItem>
                <SelectItem value="high">{t("High")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Run Button */}
          <Button
            onClick={runSimulation}
            className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-semibold"
          >
            <Play className="w-4 h-4 mr-2" />
            {t("Run Simulation")}
          </Button>
        </CardContent>
      </Card>

      {/* Simulated Metrics Grid */}
      {simulated && simulatedMetrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Simulated Credits Minted")}</p>
              <p className="text-2xl font-bold text-cyan-400 font-mono">{simulatedMetrics.creditsMinted}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Cost per Inference")}</p>
              <p className="text-2xl font-bold text-green-400 font-mono">{simulatedMetrics.costPerInference}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Robots Serviced")}</p>
              <p className="text-2xl font-bold text-purple-400 font-mono">{simulatedMetrics.robotsServiced}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Agent Tasks Executed")}</p>
              <p className="text-2xl font-bold text-orange-400 font-mono">{simulatedMetrics.agentTasks}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("GPU Seconds Allocated")}</p>
              <p className="text-2xl font-bold text-blue-400 font-mono">{simulatedMetrics.gpuSeconds}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Attestation Proofs")}</p>
              <p className="text-2xl font-bold text-cyan-400 font-mono">{simulatedMetrics.proofs}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Network Fee Burned")}</p>
              <p className="text-2xl font-bold text-red-400 font-mono">{simulatedMetrics.feeBurned}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500 mb-1">{t("Credits Remaining")}</p>
              <p className="text-2xl font-bold text-green-400 font-mono">{simulatedMetrics.creditsRemaining}</p>
              <Badge className="mt-2 bg-gray-500/10 text-gray-400 text-[10px]">{t("Simulation")}</Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Curve */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{t("Conversion Curve")}</CardTitle>
              <Badge className="bg-gray-500/10 text-gray-400 text-xs">{t("Simulation Data Only")}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={conversionCurveData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="neuro" stroke="#888" tick={{ fill: '#888', fontSize: 11 }} label={{ value: t('$NEURO Locked'), position: 'insideBottom', offset: -5, fill: '#888' }} />
                <YAxis stroke="#888" tick={{ fill: '#888', fontSize: 11 }} label={{ value: t('Credits Minted'), angle: -90, position: 'insideLeft', fill: '#888' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Line type="monotone" dataKey="low" stroke="#10B981" strokeWidth={2} name={t("Low Network Load")} dot={false} />
                <Line type="monotone" dataKey="normal" stroke={COLORS.primary} strokeWidth={2} name={t("Normal Network Load")} dot={false} />
                <Line type="monotone" dataKey="high" stroke="#F97316" strokeWidth={2} name={t("High Network Load")} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-500 text-center mt-2">
              {t("Rates shown are testnet simulations and subject to governance before mainnet")}
            </p>
          </CardContent>
        </Card>

        {/* Supply Flow */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{t("Supply Flow Visualization")}</CardTitle>
              <Badge className="bg-gray-500/10 text-gray-400 text-xs">{t("Simulation Data Only")}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={supplyFlowData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="month" stroke="#888" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis stroke="#888" tick={{ fill: '#888', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                />
                <Legend />
                <Area type="monotone" dataKey="locked" stackId="1" stroke={COLORS.purple} fill={COLORS.purple} fillOpacity={0.6} name={t("Locked $NEURO")} />
                <Area type="monotone" dataKey="issued" stackId="1" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.6} name={t("Credits Issued")} />
                <Area type="monotone" dataKey="burned" stackId="1" stroke="#F97316" fill="#F97316" fillOpacity={0.6} name={t("Credits Burned")} />
                <Area type="monotone" dataKey="recycled" stackId="1" stroke={COLORS.secondary} fill={COLORS.secondary} fillOpacity={0.6} name={t("Recycled to Pool")} />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-500 text-center mt-2">
              {t("Illustrative preview of protocol flows. Contracts not yet deployed.")}
            </p>
          </CardContent>
        </Card>

        {/* Consumption Breakdown */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{t("Credit Consumption Breakdown")}</CardTitle>
              <Badge className="bg-gray-500/10 text-gray-400 text-xs">{t("Simulation Data Only")}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={consumptionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {consumptionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Attestation Counters */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{t("Attestation Counters")}</CardTitle>
              <Badge className="bg-gray-500/10 text-gray-400 text-xs">{t("Simulated")}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
              <p className="text-xs text-cyan-300 mb-1">{t("Proof of Inference Generated")}</p>
              <p className="text-3xl font-bold text-cyan-400 font-mono">2,547,891</p>
            </div>
            <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
              <p className="text-xs text-purple-300 mb-1">{t("Proof of Action Generated")}</p>
              <p className="text-3xl font-bold text-purple-400 font-mono">1,823,456</p>
            </div>
            <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
              <p className="text-xs text-green-300 mb-1">{t("Audit Records Stored")}</p>
              <p className="text-3xl font-bold text-green-400 font-mono">4,371,347</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Testnet Disclosure */}
      <Card className="bg-yellow-950/20 border-yellow-500/30">
        <CardHeader>
          <CardTitle className="text-yellow-300 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            {t("Testnet Disclosure")}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-300 space-y-3">
          <p>
            <strong className="text-yellow-200">{t("Compute Credits are not currently mintable.")}</strong>
          </p>
          <p>
            {t("The minting mechanism using $NEURO tokens is under active development and has not yet been deployed on mainnet.")}
          </p>
          <p>
            {t("All charts, counters, and projections shown in this section are generated by a simulation engine designed to preview potential protocol dynamics.")}
          </p>
          <p>
            <strong className="text-white">{t("These values do not represent real network usage or token flows.")}</strong>
          </p>
          <p>
            {t("Once live, Compute Credit minting will be governed by onchain contracts, published conversion formulas, and public audits.")}
          </p>
        </CardContent>
      </Card>

      {/* Coming Soon */}
      <Card className="bg-gradient-to-br from-purple-950/30 to-cyan-950/30 border-purple-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            {t("Mainnet Launch Will Introduce")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start gap-3">
              <span className="text-purple-400 mt-1">•</span>
              <span><strong className="text-white">{t("Immutable lifetime credit counters")}</strong> {t("per wallet")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-cyan-400 mt-1">•</span>
              <span><strong className="text-white">{t("Onchain minting contracts")}</strong> {t("with transparent conversion formulas")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-400 mt-1">•</span>
              <span><strong className="text-white">{t("Burn and recycle mechanics")}</strong> {t("for protocol sustainability")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-orange-400 mt-1">•</span>
              <span><strong className="text-white">{t("Governance controlled conversion curves")}</strong> {t("voted by $NEURO holders")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-400 mt-1">•</span>
              <span><strong className="text-white">{t("Public dashboards")}</strong> {t("with real-time network analytics")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-purple-400 mt-1">•</span>
              <span><strong className="text-white">{t("Proof explorer")}</strong> {t("for every credit spent")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-cyan-400 mt-1">•</span>
              <span><strong className="text-white">{t("Historical network analytics")}</strong> {t("and attribution")}</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-400 mt-1">•</span>
              <span><strong className="text-white">{t("Robot node revenue attribution")}</strong> {t("for compute providers")}</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Credits;
